/**
 * 個人レポート v2 のデータ取得。
 *
 * モバイルの2つの入口で共有する。
 *  ・本人のレポート        … /embed/report/me（履歴からいちばん新しい gb74 の回答を採る）
 *  ・社員のレポート(社長用) … /embed/report/employee/:userId
 *
 * 算出・文言は gb74ReportV2 / personalTextsV2 に閉じているので、ここは取得だけを行う。
 * PC（PDF: Gb74PersonalReportPdf）とまったく同じモジュールを使うため、
 * モバイルとPCで結論が食い違うことはない。
 */
import { useCallback, useEffect, useState } from 'react';
import { analysisApi } from '../api/analysisApi';
import { historyApi } from '../api/historyApi';
import { unwrapGb74Scores, type Gb74Scores } from '../utils/gb74Report';
import { buildGb74ReportV2, type Gb74ReportV2 } from '../utils/gb74ReportV2';
import { pickPersonalTexts, type PersonalTextsV2 } from '../utils/personalTextsV2';

/** どちらのレポートを出すか。 */
export type PersonalReportTarget = { kind: 'me' } | { kind: 'user'; userId: string };

export interface PersonalReportV2Data {
  report: Gb74ReportV2;
  texts: PersonalTextsV2;
  /** ヘッダー用。取れないものは undefined */
  userName?: string;
  answerDate?: string;
  companyName?: string;
  departmentName?: string;
}

export interface PersonalReportV2State {
  data: PersonalReportV2Data | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** 「2026.08.22」形式。 */
const formatDate = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

/** gb74 のスコアから v2 の算出結果と文言を組み立てる。 */
function assemble(
  scores: Gb74Scores,
  meta: Omit<PersonalReportV2Data, 'report' | 'texts'>
): PersonalReportV2Data {
  const report = buildGb74ReportV2(scores);
  return { report, texts: pickPersonalTexts(report), ...meta };
}

export function usePersonalReportV2Data(target: PersonalReportTarget): PersonalReportV2State {
  const kind = target.kind;
  const userId = target.kind === 'user' ? target.userId : undefined;

  const [data, setData] = useState<PersonalReportV2Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (kind === 'user') {
        if (!userId) {
          setError('社員が指定されていません。');
          return;
        }
        const result = await analysisApi.getLatestResult(userId);
        const scores = unwrapGb74Scores(result.scores);
        if (!scores) {
          setError('この社員の GB人財 の回答が見つかりません。');
          return;
        }
        setData(
          assemble(scores, {
            userName: result.userName ?? undefined,
            answerDate: formatDate(result.createdAt),
            companyName: result.companyName ?? undefined,
            departmentName: result.departmentName ?? undefined,
          })
        );
        return;
      }

      // 本人。トークンから本人を判定するので userId は要らない。
      // 履歴には GB人財 以外の質問セットも混ざるため、gb74 として読めるものだけを対象にする。
      const page = await historyApi.getHistory(1, 20);
      const item = page.items.find((h) => unwrapGb74Scores(h.analysisResult?.scores) !== null);
      const scores = item ? unwrapGb74Scores(item.analysisResult?.scores) : null;
      if (!scores) {
        setError(
          'まだ GB人財 の回答がありません。回答が完了すると、ここにレポートが表示されます。'
        );
        return;
      }
      setData(assemble(scores, { answerDate: formatDate(item?.completedAt) }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'レポートの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [kind, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: () => void load() };
}
